import React, { useState, useRef, ChangeEvent } from 'react';
import { Modal, Button, Form, Alert, Spinner, ProgressBar } from 'react-bootstrap';
import axios from 'axios';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { API } from '../config';

const APP_VERSION: string = (require('../../package.json') as { version: string }).version;

const dataUrlToFile = (dataUrl: string, filename: string): File => {
  const [header, data] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)?.[1] ?? 'image/jpeg';
  const binary = atob(data);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i);
  return new File([array], filename, { type: mime });
};

/**
 * Enlace para abrir la imagen en la vista de Drive. El id sale del
 * webContentLink (…/uc?id=XXX&export=download). Si no hay id, se usa la
 * imagen directa (fullImageLink) para que igual se pueda ver.
 */
const enlaceVistaDrive = (webContentLink?: string, fullImageLink?: string): string => {
  const id = String(webContentLink || '').match(/[?&]id=([^&]+)/)?.[1];
  if (id) return `https://drive.google.com/file/d/${id}/view`;
  return fullImageLink || webContentLink || '';
};

type Cara = 'frontal' | 'posterior';

/** Campos con los que un webhook puede devolver los enlaces de Drive. */
interface EnlacesDrive {
  webContentLink?: string;
  fullImageLink?: string;
  cedula_foto_frontal?: string;
  cedula_foto_posterior?: string;
  photoLink?: string; // foto de perfil de la cuenta de Drive: NO se usa
}

/**
 * Imagen directa de una cara, según cómo la nombre el webhook:
 * `cedula_foto_frontal` / `cedula_foto_posterior` (formato actual de n8n)
 * o `fullImageLink` (formato anterior). Es el enlace que va en el registro.
 */
const imagenDirecta = (cara: Cara, datos?: EnlacesDrive | null): string => {
  if (!datos) return '';
  const propio = cara === 'frontal' ? datos.cedula_foto_frontal : datos.cedula_foto_posterior;
  return propio || datos.fullImageLink || '';
};

interface InstalacionesModalProps {
  show: boolean;
  onHide: () => void;
}

// Los enlaces son opcionales: el escaneo ya no sube a Drive cuando existe el
// webhook separado, así que pueden no venir.
interface CedulaFrontalData extends EnlacesDrive {
  cedula_instalacion: string;
  nombres_isntalacion: string;
  fecha_nacimiento: string;
  edad: number;
}

type CedulaPosteriorData = EnlacesDrive;

interface FormData {
  direccion: string;
  telefono: string;
  whatsapp: string;
  email: string;
  plan: string;
  vendedor: string;
  comentario: string;
}

const INITIAL_FORM_DATA: FormData = {
  direccion: '',
  telefono: '',
  whatsapp: '',
  email: '',
  plan: '',
  vendedor: '',
  comentario: '',
};

const PLANES = [
  { value: '', label: 'Seleccionar un plan' },
  { value: 'plan_15', label: 'Plan $15 - 150 Megas' },
  { value: 'plan_16', label: 'Plan $16 - 200 Megas' },
  { value: 'plan_18', label: 'Plan $18 - 400 Megas' },
  { value: 'plan_20', label: 'Plan $20 - 550 Megas' },
  { value: 'plan_21', label: 'Plan $21 - 600 Megas' },
  { value: 'plan_25', label: 'Plan $25 - 800 Megas' },
];

const VENDEDORES = [
  { value: '', label: 'Seleccionar un vendedor' },
  { value: 'javier_fernando_loor', label: 'JAVIER FERNANDO LOOR ZAMBRANO' },
  { value: 'diana_lorena_loor', label: 'DIANA LORENA LOOR ZAMBRANO' },
  { value: 'daniel_antonio_loor', label: 'DANIEL ANTONIO LOOR ZAMBRANO' },
  { value: 'jose_flavio_vera', label: 'JOSE FLAVIO VERA BERMEO' },
  { value: 'maria_jimena_mendoza', label: 'MARIA JIMENA MENDOZA NAVARRETE' },
  { value: 'luis_miguel_loor', label: 'LUIS MIGUEL LOOR MONCAYO' },
  { value: 'darwin_andres_vera', label: 'DARWIN ANDRES VERA LOOR' },
];

const InstalacionesModal: React.FC<InstalacionesModalProps> = ({ show, onHide }) => {
  const [step, setStep] = useState(1);

  // Datos extraídos de las APIs
  const [frontalData, setFrontalData] = useState<CedulaFrontalData | null>(null);
  const [posteriorData, setPosteriorData] = useState<CedulaPosteriorData | null>(null);

  // Preview de imágenes
  const [previewFrontal, setPreviewFrontal] = useState<string>('');
  const [previewPosterior, setPreviewPosterior] = useState<string>('');

  // Datos del formulario (paso 3)
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);

  // UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  /**
   * Subida de las imágenes a Drive, en segundo plano.
   *
   * El escaneo y la subida a Drive están en webhooks separados: el escaneo
   * responde rápido y muestra los datos, mientras la imagen se sube sola
   * mientras el vendedor sigue llenando el formulario. El enlace de Drive
   * solo hace falta al final, así que nadie espera por él.
   *
   * Si la subida falla, los datos ya escaneados NO se pierden: se puede
   * reintentar solo la subida, sin repetir la foto.
   */
  type EstadoSubida = 'inactivo' | 'subiendo' | 'listo' | 'error';
  // enlace    → el que se manda en el registro final (imagen directa)
  // enlaceVer → el que abre el archivo en Drive, para revisarlo desde la app
  type Subida = { estado: EstadoSubida; enlace: string; enlaceVer: string; archivo: File | null };
  const SUBIDA_INICIAL: Subida = { estado: 'inactivo', enlace: '', enlaceVer: '', archivo: null };

  const [subidaFrontal, setSubidaFrontal] = useState<Subida>(SUBIDA_INICIAL);
  const [subidaPosterior, setSubidaPosterior] = useState<Subida>(SUBIDA_INICIAL);

  // La fuente de verdad de cada subida vive en un ref, no en el estado de
  // React: el estado es para pintar; el envío final NO puede depender de él,
  // porque el handleSubmit que está esperando capturó una copia vieja y diría
  // que no hay enlace aunque sí lo haya. El ref siempre está al día.
  //   gen     → número de la subida vigente; una respuesta de una subida
  //             anterior (foto retomada, modal cerrado) se ignora
  //   promesa → RESUELVE CON EL ENLACE ('' si falló); nunca rechaza
  //   enlace  → último enlace bueno
  //   archivo → la foto, para poder reintentar sin volver a pedirla
  type SubidaRef = { gen: number; promesa: Promise<string> | null; enlace: string; archivo: File | null };
  const subidaRefInicial = (): SubidaRef => ({ gen: 0, promesa: null, enlace: '', archivo: null });
  const subidas = useRef<{ frontal: SubidaRef; posterior: SubidaRef }>({
    frontal: subidaRefInicial(),
    posterior: subidaRefInicial(),
  });

  const urlDrive = (cara: 'frontal' | 'posterior') =>
    cara === 'frontal' ? API.cedulaFrontalDrive : API.cedulaPosteriorDrive;

  /**
   * Sube una imagen a Drive y devuelve el enlace. Lanza si algo falla.
   *
   * De la respuesta se usan solo dos campos:
   *   cedula_foto_frontal / cedula_foto_posterior (o fullImageLink)
   *                  → la imagen directa; es el que se manda en el registro
   *   webContentLink → de aquí sale el id para abrir el archivo en Drive
   * photoLink NO se usa: es la foto de perfil de la cuenta de Drive, no la cédula.
   */
  const subirADrive = async (cara: Cara, url: string, archivo: File): Promise<{ enlace: string; enlaceVer: string }> => {
    const fd = new FormData();
    fd.append('file', archivo);
    const res = await axios.post(url, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000, // Drive puede tardar más que el escaneo
    });
    const datos: EnlacesDrive | undefined = Array.isArray(res.data) ? res.data[0] : res.data;
    const imagen = imagenDirecta(cara, datos);
    const enlace = imagen || datos?.webContentLink || '';
    if (!enlace) throw new Error('La subida no devolvió el enlace de la imagen');

    return { enlace, enlaceVer: enlaceVistaDrive(datos?.webContentLink, imagen) };
  };

  /**
   * Lanza la subida sin bloquear la interfaz. Devuelve la promesa (resuelve
   * con el enlace, '' si falló) o null si no hay webhook configurado.
   */
  const lanzarSubida = (cara: 'frontal' | 'posterior', archivo: File): Promise<string> | null => {
    const url = urlDrive(cara);
    const setSubida = cara === 'frontal' ? setSubidaFrontal : setSubidaPosterior;
    const ref = subidas.current[cara];

    ref.archivo = archivo;

    // Sin webhook configurado: se mantiene el comportamiento anterior
    // (el enlace lo trae la respuesta del escaneo).
    if (!url) return null;

    const gen = ++ref.gen;
    ref.enlace = '';
    setSubida({ estado: 'subiendo', enlace: '', enlaceVer: '', archivo });

    const promesa = subirADrive(cara, url, archivo)
      .then(({ enlace, enlaceVer }) => {
        if (gen !== ref.gen) return ''; // llegó tarde: ya hay otra subida vigente
        ref.enlace = enlace;
        setSubida({ estado: 'listo', enlace, enlaceVer, archivo });
        return enlace;
      })
      .catch(() => {
        if (gen !== ref.gen) return '';
        setSubida({ estado: 'error', enlace: '', enlaceVer: '', archivo });
        return '';
      });

    ref.promesa = promesa;
    return promesa;
  };

  /** Reintenta solo la subida, sin volver a pedir la foto. */
  const reintentarSubida = (cara: 'frontal' | 'posterior') => {
    const archivo = subidas.current[cara].archivo;
    if (archivo) lanzarSubida(cara, archivo);
  };

  /**
   * Enlace definitivo de una cara para el registro final. Orden:
   *   1. lo que resolvió la subida en curso (siempre fresco)
   *   2. el último enlace bueno del ref
   *   3. si falló y aún tenemos la foto, se reintenta UNA vez aquí mismo,
   *      sin pedirle nada al vendedor
   *   4. sin subida separada configurada: el enlace que trajo el escaneo
   */
  const obtenerEnlaceFinal = async (cara: Cara, escaneo: EnlacesDrive): Promise<string> => {
    const ref = subidas.current[cara];
    let enlace = (await ref.promesa) || ref.enlace;
    if (!enlace && ref.archivo && urlDrive(cara)) {
      enlace = (await lanzarSubida(cara, ref.archivo)) || '';
    }
    return enlace || imagenDirecta(cara, escaneo) || escaneo.webContentLink || '';
  };

  /**
   * Estado de las imágenes en Drive, compartido por los pasos 2 y 3.
   * Se suben por detrás mientras el vendedor sigue; solo estorba si algo falló.
   */
  const renderEstadoSubida = () => {
    // Enlace para ver cada cara en Drive: el de la subida separada si existe;
    // si no (subida no configurada), el que trajo la respuesta del escaneo.
    const caras = [
      { nombre: 'frontal', s: subidaFrontal, cara: 'frontal' as const,
        ver: subidaFrontal.enlaceVer || enlaceVistaDrive(frontalData?.webContentLink, imagenDirecta('frontal', frontalData)) },
      { nombre: 'posterior', s: subidaPosterior, cara: 'posterior' as const,
        ver: subidaPosterior.enlaceVer || enlaceVistaDrive(posteriorData?.webContentLink, imagenDirecta('posterior', posteriorData)) },
    ];
    const fallaron = caras.filter(c => c.s.estado === 'error');
    const subiendo = caras.some(c => c.s.estado === 'subiendo');
    const haySubidaActiva = caras.some(c => c.s.estado !== 'inactivo');
    // Caras que ya se pueden abrir en Drive (no en curso ni fallidas)
    const conEnlace = caras.filter(c => c.ver && c.s.estado !== 'subiendo' && c.s.estado !== 'error');

    if (!haySubidaActiva && conEnlace.length === 0) return null;

    if (fallaron.length) {
      return (
        <Alert variant="warning" className="mb-3 py-2">
          <small>
            No se pudo guardar la cédula {fallaron.map(c => c.nombre).join(' y ')} en Drive.
            Los datos de la cédula NO se perdieron.
          </small>
          <div className="mt-2 d-flex gap-2">
            {fallaron.map(c => (
              <Button key={c.cara} size="sm" variant="outline-secondary" onClick={() => reintentarSubida(c.cara)}>
                Reintentar {c.nombre}
              </Button>
            ))}
          </div>
        </Alert>
      );
    }
    return (
      <div className="mb-3" style={{ fontSize: 12, color: 'var(--sn-texto-3)' }}>
        {haySubidaActiva && (subiendo
          ? <><Spinner animation="border" size="sm" className="me-2" />Guardando cédula en Drive…</>
          : <>Cédula guardada en Drive ✓</>)}
        {/* Enlaces sutiles para revisar cada cara en Drive, siempre identificadas
            como frontal / posterior. Se abren fuera de la app ('_system'),
            igual que los demás enlaces externos. */}
        {!subiendo && conEnlace.length > 0 && (
          <div className="mt-1 d-flex gap-3 flex-wrap">
            {conEnlace.map(c => (
              <button
                key={c.cara}
                type="button"
                onClick={() => window.open(c.ver, '_system')}
                style={{ background: 'none', border: 'none', padding: 0, fontSize: 12, color: 'var(--sn-texto-3)', textDecoration: 'underline', cursor: 'pointer' }}
              >
                Ver cédula {c.nombre} en Drive
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Refs para limpiar inputs
  const frontalRef = useRef<HTMLInputElement>(null);
  const posteriorRef = useRef<HTMLInputElement>(null);
  const [camError, setCamError] = useState<string | null>(null);

  // Foto frontal ampliada en el paso 2 (para revisarla sin volver atrás).
  const [fotoFrontalAmpliada, setFotoFrontalAmpliada] = useState(false);

  const progress = [0, 25, 50, 75, 100][step];

  // Validaciones
  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isValidPhone = (phone: string): boolean => {
    return /^[0-9]{7,}$/.test(phone.replace(/\D/g, ''));
  };

  const processFrontalFile = async (file: File) => {
    const preview = URL.createObjectURL(file);
    setPreviewFrontal(preview);

    setLoading(true);
    setError(null);

    try {
      const fd = new FormData();
      fd.append('file', file);

      const response = await axios.post(API.cedulaFrontal, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 45000,
      });

      // Maneja tanto array como objeto directo
      let data: CedulaFrontalData;

      if (Array.isArray(response.data)) {
        if (response.data.length === 0) {
          setError('La API no devolvió datos válidos');
          return;
        }
        data = response.data[0] as CedulaFrontalData;
      } else {
        data = response.data as CedulaFrontalData;
      }

      // Validar que tenga los campos requeridos
      if (!data?.cedula_instalacion || !data?.nombres_isntalacion) {
        setError('No se pudieron extraer los datos de la cédula. Intenta con una imagen más clara.');
        return;
      }

      setFrontalData(data);
      setError(null);
      // La imagen se sube a Drive por detrás; no se espera para avanzar.
      lanzarSubida('frontal', file);
      setStep(2);
    } catch (err) {
      let errorMessage = 'Error al procesar la cédula frontal';

      if (axios.isAxiosError(err)) {
        if (err.response?.status === 413) {
          errorMessage = 'El archivo es demasiado grande';
        } else if (err.response?.status === 400) {
          errorMessage =
            err.response.data?.message ||
            'No se pudo leer la cédula. Intenta con una imagen más clara';
        } else if (err.code === 'ECONNABORTED') {
          errorMessage = 'La solicitud tardó demasiado. Intenta nuevamente';
        } else if (err.response?.status === 500) {
          errorMessage = 'Error en el servidor. Intenta nuevamente';
        } else if (err.message === 'Network Error') {
          errorMessage = 'Error de conexión. Verifica tu internet';
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessFrontal = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processFrontalFile(file);
  };

  const handleCamaraFrontal = async (source: CameraSource) => {
    setCamError(null);
    try {
      const photo = await Camera.getPhoto({ quality: 80, resultType: CameraResultType.DataUrl, source, correctOrientation: true });
      if (photo.dataUrl) await processFrontalFile(dataUrlToFile(photo.dataUrl, 'cedula_frontal.jpg'));
    } catch { setCamError('No se pudo acceder a la cámara/galería.'); }
  };

  // Paso 2: Procesar cédula posterior
  const processPosteriorFile = async (file: File) => {

    // Mostrar preview
    const preview = URL.createObjectURL(file);
    setPreviewPosterior(preview);

    setLoading(true);
    setError(null);

    try {
      const fd = new FormData();
      fd.append('file', file);

      const response = await axios.post(API.cedulaPosterior, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 45000,
      });

      // Maneja tanto array como objeto directo
      let data: CedulaPosteriorData;

      if (Array.isArray(response.data)) {
        if (response.data.length === 0) {
          setError('La API no devolvió datos válidos');
          return;
        }
        data = response.data[0] as CedulaPosteriorData;
      } else {
        data = response.data as CedulaPosteriorData;
      }

      setPosteriorData(data);
      setError(null);
      // Igual que en la frontal: la subida a Drive va por detrás.
      lanzarSubida('posterior', file);
      setStep(3);
    } catch (err) {
      let errorMessage = 'Error al procesar la cédula posterior';

      if (axios.isAxiosError(err)) {
        if (err.response?.status === 413) {
          errorMessage = 'El archivo es demasiado grande';
        } else if (err.response?.status === 400) {
          errorMessage =
            err.response.data?.message ||
            'No se pudo leer la cédula. Intenta con una imagen más clara';
        } else if (err.code === 'ECONNABORTED') {
          errorMessage = 'La solicitud tardó demasiado. Intenta nuevamente';
        } else if (err.response?.status === 500) {
          errorMessage = 'Error en el servidor. Intenta nuevamente';
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessPosterior = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processPosteriorFile(file);
  };

  const handleCamaraPosterior = async (source: CameraSource) => {
    setCamError(null);
    try {
      const photo = await Camera.getPhoto({ quality: 80, resultType: CameraResultType.DataUrl, source, correctOrientation: true });
      if (photo.dataUrl) await processPosteriorFile(dataUrlToFile(photo.dataUrl, 'cedula_posterior.jpg'));
    } catch { setCamError('No se pudo acceder a la cámara/galería.'); }
  };

  // Paso 3: Enviar datos finales
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!frontalData || !posteriorData) {
      setError('Faltan datos de las cédulas');
      return;
    }

    // Validaciones
    if (!formData.direccion.trim()) {
      setError('La dirección es requerida');
      return;
    }
    if (!isValidPhone(formData.telefono)) {
      setError('Teléfono inválido (mínimo 7 dígitos)');
      return;
    }
    if (!isValidPhone(formData.whatsapp)) {
      setError('WhatsApp inválido (mínimo 7 dígitos)');
      return;
    }
    if (!isValidEmail(formData.email)) {
      setError('Email inválido');
      return;
    }
    if (!formData.plan) {
      setError('Debe seleccionar un plan');
      return;
    }
    if (!formData.vendedor) {
      setError('Debe seleccionar un vendedor');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Las imágenes se subieron a Drive por detrás mientras se llenaba el
      // formulario. Aquí es el único punto donde el enlace hace falta: si
      // alguna subida sigue en curso se espera, y si falló se reintenta sola.
      const [enlaceFrontal, enlacePosterior] = await Promise.all([
        obtenerEnlaceFinal('frontal', frontalData),
        obtenerEnlaceFinal('posterior', posteriorData),
      ]);

      // Nunca se manda un registro con una cédula sin enlace: se avisa qué
      // cara falta y por qué, para que el aviso sea accionable.
      const faltan = ([['frontal', enlaceFrontal], ['posterior', enlacePosterior]] as const)
        .filter(([, enlace]) => !enlace)
        .map(([cara]) => cara);
      if (faltan.length) {
        const sinWebhook = faltan.filter(cara => !urlDrive(cara));
        setError(sinWebhook.length
          ? `Esta versión no tiene configurada la subida a Drive de la cédula ${sinWebhook.join(' y ')} `
            + `(falta REACT_APP_CEDULA_${sinWebhook[0].toUpperCase()}_DRIVE_URL) y el escaneo ya no devuelve el enlace.`
          : `No se pudo guardar la cédula ${faltan.join(' y ')} en Drive. Reintenta la subida antes de continuar.`);
        setLoading(false);
        return;
      }

      const payload = {
        nombres: frontalData.nombres_isntalacion,
        cedula: frontalData.cedula_instalacion,
        fecha_nacimiento: frontalData.fecha_nacimiento,
        edad: frontalData.edad,
        direccion: formData.direccion,
        telefono: formData.telefono,
        whatsapp: formData.whatsapp,
        email: formData.email,
        plan: formData.plan,
        vendedor: formData.vendedor,
        comentario: formData.comentario,
        cedula_foto_frontal: enlaceFrontal,
        cedula_foto_posterior: enlacePosterior,
        // Versión de la app que hizo el registro: en n8n se ve de un vistazo
        // si un envío incompleto vino de un APK viejo.
        app_version: APP_VERSION,
      };

      await axios.post(API.instalaciones, payload, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 30000,
      });

      setSuccess(true);
      setStep(4);
    } catch (err) {
      let errorMessage = 'Error al enviar datos de instalación';

      if (axios.isAxiosError(err)) {
        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response?.status === 409) {
          errorMessage = 'Este cliente ya está registrado';
        } else if (err.code === 'ECONNABORTED') {
          errorMessage = 'La solicitud tardó demasiado';
        } else if (err.response?.status === 500) {
          errorMessage = 'Error en el servidor. Intenta nuevamente';
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Limpiar previews y resetear modal
  const handleClose = () => {
    if (previewFrontal) URL.revokeObjectURL(previewFrontal);
    if (previewPosterior) URL.revokeObjectURL(previewPosterior);

    if (step > 1 && !success) {
      if (window.confirm('¿Estás seguro? Perderás los datos ingresados.')) {
        resetModal();
        onHide();
      }
    } else {
      resetModal();
      onHide();
    }
  };

  // Resetear estado del modal
  const resetModal = () => {
    setStep(1);
    setFrontalData(null);
    setPosteriorData(null);
    setPreviewFrontal('');
    setPreviewPosterior('');
    setFormData(INITIAL_FORM_DATA);
    setLoading(false);
    setError(null);
    setSuccess(false);
    // Sin esto, una instalación nueva heredaría el enlace de Drive de la anterior.
    setSubidaFrontal(SUBIDA_INICIAL);
    setSubidaPosterior(SUBIDA_INICIAL);
    // Se conserva gen (y se sube) para que una subida que siga en vuelo del
    // modal anterior no pinte su enlace sobre la instalación nueva.
    // Se muta el mismo objeto (no se reemplaza): las promesas en vuelo
    // guardan una referencia a él y comparan contra este gen.
    (['frontal', 'posterior'] as const).forEach(cara => {
      Object.assign(subidas.current[cara], subidaRefInicial(), { gen: subidas.current[cara].gen + 1 });
    });
    if (frontalRef.current) frontalRef.current.value = '';
    if (posteriorRef.current) posteriorRef.current.value = '';
  };

  // Cambiar campo de formulario
  const handleFormChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  // Volver al paso anterior
  const handlePrevStep = () => {
    if (step > 1) {
      setStep(step - 1);
      setError(null);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" scrollable centered>
      <Modal.Header closeButton>
        <Modal.Title>Registrar Instalación</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ProgressBar now={progress} className="mb-4" label={`Paso ${step}`} />

        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}
        {success && step !== 4 && (
          <Alert variant="success">¡Datos procesados exitosamente!</Alert>
        )}

        {/* Paso 1: Cédula Frontal */}
        {step === 1 && (
          <div>
            <h5 className="mb-3">📸 Paso 1: Cédula Frontal</h5>
            <p className="text-muted mb-3">
              Sube una foto clara de la cédula frontal. Se extraerán automáticamente tus datos
              personales.
            </p>

            {previewFrontal && (
              <div className="d-flex align-items-center gap-2 mb-3 p-2 rounded" style={{ background: 'var(--sn-superficie-2)', border: '1px solid var(--sn-borde)' }}>
                <img src={previewFrontal} alt="Preview Frontal" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--sn-borde)', flexShrink: 0 }} />
                <span style={{ fontSize: '0.8rem', color: 'var(--sn-texto-2)' }}>Vista previa — cédula frontal</span>
              </div>
            )}

            {camError && <Alert variant="danger" className="py-2">{camError}</Alert>}

            <p className="text-muted small mb-2">¿Cómo deseas capturar la cédula frontal?</p>
            <div className="d-flex gap-2 mb-3">
              <button onClick={() => handleCamaraFrontal(CameraSource.Camera)} disabled={loading}
                style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'0.3rem', padding:'0.6rem 0.4rem', background:'var(--sn-marca-suave)', border:'2px solid var(--sn-marca)', borderRadius:12, cursor:'pointer', fontSize:'0.78rem', color:'var(--sn-marca-texto)', fontWeight:500 }}>
                <span style={{ fontSize:'1.8rem' }}>📷</span><span>Tomar foto</span>
              </button>
              <button onClick={() => handleCamaraFrontal(CameraSource.Photos)} disabled={loading}
                style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'0.3rem', padding:'0.6rem 0.4rem', background:'var(--sn-marca-suave)', border:'2px solid var(--sn-marca)', borderRadius:12, cursor:'pointer', fontSize:'0.78rem', color:'var(--sn-marca-texto)', fontWeight:500 }}>
                <span style={{ fontSize:'1.8rem' }}>🖼️</span><span>Galería</span>
              </button>
            </div>

            {loading && (
              <div className="text-center mb-3">
                <Spinner animation="border" className="me-2" />
                <span>Procesando cédula frontal...</span>
              </div>
            )}

            <div className="d-flex justify-content-between">
              <Button variant="secondary" onClick={handleClose} disabled={loading}>
                Cancelar
              </Button>
            </div>
          </div>
        )}

        {/* Paso 2: Cédula Posterior */}
        {step === 2 && (
          <div>
            <h5 className="mb-3">📸 Paso 2: Cédula Posterior</h5>

            {frontalData && (
              <Alert variant="info" className="mb-3">
                <strong>Datos extraídos:</strong>
                <br />
                <small>
                  <strong>Nombre:</strong> {frontalData.nombres_isntalacion}
                  <br />
                  <strong>Cédula:</strong> {frontalData.cedula_instalacion}
                  <br />
                  <strong>Fecha de nacimiento:</strong>{' '}
                  {new Date(frontalData.fecha_nacimiento).toLocaleDateString('es-ES')}
                  <br />
                  <strong>Edad:</strong> {frontalData.edad} años
                </small>
              </Alert>
            )}

            {/* Foto frontal: miniatura tocable para revisarla sin volver atrás.
                Se usa la vista previa local (instantánea), no el enlace de Drive,
                que puede no existir todavía. */}
            {previewFrontal && (
              <div
                className="d-flex align-items-center gap-2 mb-2 p-2 rounded"
                style={{ background: 'var(--sn-superficie-2)', border: '1px solid var(--sn-borde)', cursor: 'pointer' }}
                onClick={() => setFotoFrontalAmpliada(v => !v)}
                role="button"
                aria-label={fotoFrontalAmpliada ? 'Reducir foto frontal' : 'Ver foto frontal'}
              >
                <img
                  src={previewFrontal}
                  alt="Cédula frontal"
                  style={fotoFrontalAmpliada
                    ? { width: '100%', height: 'auto', borderRadius: 8, border: '1px solid var(--sn-borde)' }
                    : { width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--sn-borde)', flexShrink: 0 }}
                />
                {!fotoFrontalAmpliada && (
                  <span style={{ fontSize: '0.8rem', color: 'var(--sn-texto-2)' }}>🔍 Ver foto frontal</span>
                )}
              </div>
            )}

            {renderEstadoSubida()}

            {(subidaFrontal.estado === 'listo' || subidaFrontal.estado === 'inactivo') ? (
              <p className="text-muted mb-3">
                Ahora sube una foto clara de la cédula posterior.
              </p>
            ) : subidaFrontal.estado === 'subiendo' ? (
              <p className="text-muted mb-3">
                Espera un momento a que la cédula frontal termine de guardarse…
              </p>
            ) : null}

            {previewPosterior && (
              <div className="d-flex align-items-center gap-2 mb-3 p-2 rounded" style={{ background: 'var(--sn-superficie-2)', border: '1px solid var(--sn-borde)' }}>
                <img src={previewPosterior} alt="Preview Posterior" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--sn-borde)', flexShrink: 0 }} />
                <span style={{ fontSize: '0.8rem', color: 'var(--sn-texto-2)' }}>Vista previa — cédula posterior</span>
              </div>
            )}

            {/* Los botones para la posterior solo aparecen cuando la frontal ya
                quedó guardada en Drive (o cuando no hay subida separada
                configurada). Mientras sube, o si falló y falta reintentar,
                se ocultan para no dejar avanzar a medias. */}
            {(subidaFrontal.estado === 'listo' || subidaFrontal.estado === 'inactivo') && (
              <>
                <p className="text-muted small mb-2">¿Cómo deseas capturar la cédula posterior?</p>
                <div className="d-flex gap-2 mb-3">
                  <button onClick={() => handleCamaraPosterior(CameraSource.Camera)} disabled={loading}
                    style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'0.3rem', padding:'0.6rem 0.4rem', background:'var(--sn-marca-suave)', border:'2px solid var(--sn-marca)', borderRadius:12, cursor:'pointer', fontSize:'0.78rem', color:'var(--sn-marca-texto)', fontWeight:500 }}>
                    <span style={{ fontSize:'1.8rem' }}>📷</span><span>Tomar foto</span>
                  </button>
                  <button onClick={() => handleCamaraPosterior(CameraSource.Photos)} disabled={loading}
                    style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'0.3rem', padding:'0.6rem 0.4rem', background:'var(--sn-marca-suave)', border:'2px solid var(--sn-marca)', borderRadius:12, cursor:'pointer', fontSize:'0.78rem', color:'var(--sn-marca-texto)', fontWeight:500 }}>
                    <span style={{ fontSize:'1.8rem' }}>🖼️</span><span>Galería</span>
                  </button>
                </div>
              </>
            )}

            {loading && (
              <div className="text-center mb-3">
                <Spinner animation="border" className="me-2" />
                <span>Procesando cédula posterior...</span>
              </div>
            )}

            <div className="d-flex justify-content-between">
              <Button variant="secondary" onClick={handlePrevStep} disabled={loading}>
                Atrás
              </Button>
            </div>
          </div>
        )}

        {/* Paso 3: Datos Complementarios */}
        {step === 3 && (
          <div>
            <h5 className="mb-3">📋 Paso 3: Información del Cliente</h5>

            {frontalData && (
              <Alert variant="info" className="mb-3">
                <strong>Datos extraídos de la cédula:</strong>
                <br />
                <small>
                  <strong>Nombre Completo:</strong> {frontalData.nombres_isntalacion}
                  <br />
                  <strong>Cédula:</strong> {frontalData.cedula_instalacion}
                  <br />
                  <strong>Fecha de nacimiento:</strong>{' '}
                  {new Date(frontalData.fecha_nacimiento).toLocaleDateString('es-ES')}
                  <br />
                  <strong>Edad:</strong> {frontalData.edad} años
                </small>
              </Alert>
            )}

            {renderEstadoSubida()}

            <Form onSubmit={handleSubmit}>
              {/* Fila 1: Teléfono y WhatsApp */}
              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3" controlId="telefono">
                    <Form.Label>Teléfono</Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder="Ej: 87654321"
                      value={formData.telefono}
                      onChange={e => handleFormChange('telefono', e.currentTarget.value)}
                      disabled={loading}
                      required
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3" controlId="whatsapp">
                    <Form.Label>WhatsApp</Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder="Ej: 87654321"
                      value={formData.whatsapp}
                      onChange={e => handleFormChange('whatsapp', e.currentTarget.value)}
                      disabled={loading}
                      required
                    />
                  </Form.Group>
                </div>
              </div>

              {/* Fila 2: Email */}
              <Form.Group className="mb-3" controlId="email">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="tu@email.com"
                  value={formData.email}
                  onChange={e => handleFormChange('email', e.currentTarget.value)}
                  disabled={loading}
                  required
                />
              </Form.Group>

              {/* Fila 3: Dirección */}
              <Form.Group className="mb-3" controlId="direccion">
                <Form.Label>Dirección de instalación</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Calle, número, apto, ciudad"
                  value={formData.direccion}
                  onChange={e => handleFormChange('direccion', e.currentTarget.value)}
                  disabled={loading}
                  required
                />
              </Form.Group>

              {/* Fila 4: Plan y Vendedor */}
              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3" controlId="plan">
                    <Form.Label>Plan a contratar</Form.Label>
                    <Form.Select
                      value={formData.plan}
                      onChange={e => handleFormChange('plan', e.currentTarget.value)}
                      disabled={loading}
                      required
                    >
                      {PLANES.map(plan => (
                        <option key={plan.value} value={plan.value}>
                          {plan.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3" controlId="vendedor">
                    <Form.Label>Vendedor</Form.Label>
                    <Form.Select
                      value={formData.vendedor}
                      onChange={e => handleFormChange('vendedor', e.currentTarget.value)}
                      disabled={loading}
                      required
                    >
                      {VENDEDORES.map(vendedor => (
                        <option key={vendedor.value} value={vendedor.value}>
                          {vendedor.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
              </div>

              {/* Fila 5: Comentario */}
              <Form.Group className="mb-3" controlId="comentario">
                <Form.Label>Comentario adicional</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Comentarios o notas importantes"
                  value={formData.comentario}
                  onChange={e => handleFormChange('comentario', e.currentTarget.value)}
                  disabled={loading}
                />
              </Form.Group>

              <div className="d-flex justify-content-between gap-2 mt-4">
                <Button
                  variant="secondary"
                  onClick={handlePrevStep}
                  disabled={loading}
                >
                  Atrás
                </Button>
                <Button type="submit" variant="primary" disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Enviando...
                    </>
                  ) : (
                    'Enviar datos'
                  )}
                </Button>
              </div>
            </Form>
          </div>
        )}

        {/* Paso 4: Confirmación Exitosa */}
        {step === 4 && success && (
          <div>
            <h5 className="mb-3 text-center">✓ ¡Registro exitoso!</h5>

            <Alert variant="success" className="mb-3">
              <strong>El cliente ha sido registrado correctamente</strong>
            </Alert>

            {frontalData && (
              <div className="mb-4">
                <h6>Datos registrados:</h6>
                <table className="table table-bordered table-sm">
                  <tbody>
                    <tr>
                      <td>
                        <strong>Nombre:</strong>
                      </td>
                      <td>{frontalData.nombres_isntalacion}</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Cédula:</strong>
                      </td>
                      <td>{frontalData.cedula_instalacion}</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Fecha de nacimiento:</strong>
                      </td>
                      <td>
                        {new Date(frontalData.fecha_nacimiento).toLocaleDateString('es-ES')}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Email:</strong>
                      </td>
                      <td>{formData.email}</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Teléfono:</strong>
                      </td>
                      <td>{formData.telefono}</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Dirección:</strong>
                      </td>
                      <td>{formData.direccion}</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Plan:</strong>
                      </td>
                      <td>
                        {PLANES.find(p => p.value === formData.plan)?.label}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Vendedor:</strong>
                      </td>
                      <td>
                        {VENDEDORES.find(v => v.value === formData.vendedor)?.label}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Enlaces a las cédulas en Drive. Antes usaban webContentLink de la
                respuesta del escaneo; como ese webhook ya no sube a Drive, ese
                campo llega vacío. Ahora se usa el enlace de la subida separada
                y, si no existe, el del escaneo (flujo antiguo). Se abren fuera
                de la app ('_system'), como los demás enlaces externos. */}
            {(() => {
              const verFrontal = subidaFrontal.enlaceVer || enlaceVistaDrive(frontalData?.webContentLink, imagenDirecta('frontal', frontalData));
              const verPosterior = subidaPosterior.enlaceVer || enlaceVistaDrive(posteriorData?.webContentLink, imagenDirecta('posterior', posteriorData));
              if (!verFrontal && !verPosterior) return null;
              return (
                <div className="mb-3 text-center d-flex justify-content-center gap-2 flex-wrap">
                  {verFrontal && (
                    <Button variant="outline-secondary" size="sm" onClick={() => window.open(verFrontal, '_system')}>
                      🪪 Ver cédula frontal en Drive
                    </Button>
                  )}
                  {verPosterior && (
                    <Button variant="outline-secondary" size="sm" onClick={() => window.open(verPosterior, '_system')}>
                      🪪 Ver cédula posterior en Drive
                    </Button>
                  )}
                </div>
              );
            })()}

            <div className="text-center mt-4">
              <Button variant="primary" onClick={handleClose} size="lg">
                Cerrar
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default InstalacionesModal;